// Справочник упражнений: поиск, фильтр по нескольким группам мышц (списком или на фигуре), архив,
// свои упражнения. На телефоне — список с мини-фото, с md — таблица; ⓘ открывает технику и схему.
// Группы фильтруются по основной мышце на клиенте: справочник приходит целиком.

import { useDeferredValue, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { PageHeader } from '@/components/layout'
import { Badge, Button, Card, EmptyState, ErrorText, Icon, IconButton, Input, PageSpinner } from '@/components/ui'
import { cn } from '@/lib/cn'
import { exerciseImageUrl, useExercises, useMuscleLabels, type Exercise } from '@/pages/workouts/api'
import { EQUIPMENT_LABELS, TRACKING_LABELS } from '@/pages/workouts/logic'

import { ExerciseForm } from './ExerciseForm'
import { ExerciseRecognition } from './ExerciseRecognition'
import { ExerciseInfoSheet } from './ExerciseInfo'
import { MuscleChips, MuscleMapFilter, MuscleMultiSelect } from './MuscleFilter'

export function ExercisesPage() {
  const navigate = useNavigate()
  const [recognizing, setRecognizing] = useState(false)
  const [q, setQ] = useState('')
  const [muscles, setMuscles] = useState<string[]>([])
  const [archived, setArchived] = useState(false)
  const [creating, setCreating] = useState(false)
  const [showMap, setShowMap] = useState(false)
  const [info, setInfo] = useState<Exercise | null>(null)
  const deferred = useDeferredValue(q)
  const exercises = useExercises(deferred.trim(), '', archived)
  const muscleLabel = useMuscleLabels()
  const list = useMemo(
    () => (muscles.length ? exercises.data?.filter((ex) => muscles.includes(ex.primary_muscle)) : exercises.data),
    [exercises.data, muscles],
  )

  return (
    <>
      <PageHeader
        title="Упражнения"
        description="Справочник: группы мышц, оборудование и сопоставление с Garmin."
        actions={
          <>
            <Button onClick={() => setRecognizing(true)}>Распознать тренажёр</Button>
            <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
              Своё упражнение
            </Button>
          </>
        }
      />
      <Card>
        <div className="grid gap-3 sm:grid-cols-[1fr_14rem_auto] sm:items-end">
          <Input label="Поиск" placeholder="Название, с первой буквы" value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="grid grid-cols-[1fr_auto] items-end gap-3 sm:contents">
            <MuscleMultiSelect value={muscles} onChange={setMuscles} />
            <label className="flex min-h-11 items-center gap-2 text-sm text-fg">
              <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} className="h-5 w-5" />
              <span className="sm:hidden">Архив</span>
              <span className="hidden sm:inline">Показать архив</span>
            </label>
          </div>
        </div>
        <div className={cn('mt-3 flex flex-wrap items-center justify-between gap-2', muscles.length === 0 && 'xl:hidden')}>
          <MuscleChips value={muscles} onChange={setMuscles} />
          <Button size="sm" icon="character" aria-expanded={showMap} onClick={() => setShowMap((v) => !v)} className="xl:hidden">
            {showMap ? 'Скрыть схему' : 'Выбрать на схеме'}
          </Button>
        </div>
      </Card>

      <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_17rem] xl:items-start">
        <aside
          aria-label="Группы мышц на схеме"
          className={cn('xl:sticky xl:top-4 xl:order-last', showMap ? 'block' : 'hidden xl:block')}
        >
          <Card title="На схеме">
            <MuscleMapFilter value={muscles} onChange={setMuscles} />
          </Card>
        </aside>
        <div className="min-w-0">
        {exercises.isPending ? (
          <PageSpinner />
        ) : exercises.error ? (
          <ErrorText error={exercises.error} />
        ) : !list || list.length === 0 ? (
          <Card>
            <EmptyState icon="list" title="Ничего не нашлось" description="Измени запрос или добавь своё упражнение." />
          </Card>
        ) : (
          <>
          <Card flush className="md:hidden">
            <ul className="divide-y divide-border">
              {list.map((ex) => (
                <li key={ex.id}>
                  <ExerciseRow exercise={ex} muscle={muscleLabel(ex.primary_muscle)} onInfo={() => setInfo(ex)} />
                </li>
              ))}
            </ul>
          </Card>
          <Card flush className="hidden md:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-muted">
                  <tr className="border-b border-border">
                    <th className="px-4 py-2 font-medium">Название</th>
                    <th className="px-4 py-2 font-medium">Мышцы</th>
                    <th className="hidden px-4 py-2 font-medium md:table-cell">Оборудование</th>
                    <th className="hidden px-4 py-2 font-medium lg:table-cell">Учёт</th>
                    <th className="hidden px-4 py-2 font-medium 2xl:table-cell">Garmin</th>
                    <th className="w-12 px-2 py-2">
                      <span className="sr-only">Подробнее</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((ex) => (
                    <tr key={ex.id} className="border-b border-border last:border-0 hover:bg-surface-2">
                      <td className="px-4 py-2.5">
                        <Link to={`/exercises/${ex.id}`} className="font-medium text-fg hover:text-accent">
                          {ex.name}
                        </Link>
                        <span className="ml-2 inline-flex gap-1">
                          {ex.is_custom ? <Badge>своё</Badge> : null}
                          {ex.archived ? <Badge tone="warning">архив</Badge> : null}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-muted">
                        <span className="text-fg">{muscleLabel(ex.primary_muscle)}</span>
                        {ex.secondary_muscles.length > 0
                          ? `, ${ex.secondary_muscles.map(muscleLabel).join(', ').toLowerCase()}`
                          : null}
                      </td>
                      <td className="hidden px-4 py-2.5 text-muted md:table-cell">{EQUIPMENT_LABELS[ex.equipment]}</td>
                      <td className="hidden px-4 py-2.5 text-muted lg:table-cell">{TRACKING_LABELS[ex.tracking]}</td>
                      <td className="hidden px-4 py-2.5 font-mono text-xs text-muted 2xl:table-cell">
                        {ex.garmin_name ?? ex.garmin_category ?? '—'}
                      </td>
                      <td className="px-2 py-1 text-right">
                        <IconButton icon="info" label={`Техника и мышцы: ${ex.name}`} onClick={() => setInfo(ex)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          </>
        )}
        </div>
      </div>
      {info ? <ExerciseInfoSheet exerciseId={info.id} name={info.name} exercise={info} open onClose={() => setInfo(null)} /> : null}
      {creating ? <ExerciseForm open onClose={() => setCreating(false)} exercise={null} /> : null}
      {recognizing ? <ExerciseRecognition initialText={q} onClose={() => setRecognizing(false)} onPick={(exercise) => navigate(`/exercises/${exercise.id}`)} /> : null}
    </>
  )
}

function ExerciseRow({ exercise, muscle, onInfo }: { exercise: Exercise; muscle: string; onInfo: () => void }) {
  const photo = exercise.images[0]
  return (
    <div className="flex items-center pr-2">
    <Link to={`/exercises/${exercise.id}`} className="flex min-h-16 min-w-0 flex-1 items-center gap-3 py-2.5 pl-4 active:bg-surface-2">
      {photo ? (
        <img
          src={exerciseImageUrl(photo)}
          alt=""
          loading="lazy"
          className="size-12 shrink-0 rounded-lg bg-surface-2 object-cover"
        />
      ) : (
        <span className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-surface-2 text-muted">
          <Icon name="workout" size={20} />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block font-medium leading-snug text-fg">
          {exercise.name}
          {exercise.is_custom ? <Badge className="ml-2">своё</Badge> : null}
          {exercise.archived ? <Badge tone="warning" className="ml-2">архив</Badge> : null}
        </span>
        <span className="mt-0.5 block text-xs text-muted">
          {muscle} · {EQUIPMENT_LABELS[exercise.equipment]}
        </span>
      </span>
    </Link>
    <IconButton icon="info" label={`Техника и мышцы: ${exercise.name}`} onClick={onInfo} />
    </div>
  )
}
